// ===== ロット単位QRスキャン =====

import { useMemo } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import { QRCodeInput } from "../../../components/input/QRCodeInput";
import useInput from "../../../util/useInput";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { LotRow, MaterialsById, materialName } from "./helpers";
import { required } from "../../../util/typed";

export function LotScanPage({ pageContext, materialsById }: { pageContext: PageContext; materialsById: MaterialsById }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id: carryOutId, lotId } = useParams<{ id: string; lotId: string }>();
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<LotRow>(() => {
    if (!carryOutId || !lotId) {
      return Promise.resolve(null);
    }
    return api.carry_out.getFromId({ id: carryOutId }).then((co) => co.Lots.find((l) => l.id === lotId) ?? null);
  }, [api, carryOutId, lotId, retryCount]);
  const lot = state.status === "ready" ? state.data : null;

  const initialIds = useMemo(() => (lot ? lot.Materials.map((m) => m.id) : []), [lot]);
  const input = useInput<{ MaterialIDs: number[] }>({ MaterialIDs: initialIds }, () => {}, [lot]);

  const process = async (ids: number[]) => {
    try {
      await api.lot.process({ id: required(lotId, "URLのLOT ID"), MaterialIDs: ids });
      navigate(`/console/carry-out/${carryOutId}/lot/${lotId}/`);
    } catch {
      api.showError("出荷の保存に失敗しました。再度お試しください。");
    }
  };

  if (state.status !== "ready") {
    return <DetailGuard state={state} notFoundLabel={`材料ロット ${lotId}`} onRetry={retry}>{() => <></>}</DetailGuard>;
  }

  const readyLot = state.data;
  const matName = materialName(materialsById, readyLot.material_id);
  const lotNo = readyLot.lot_no ?? "-";

  return (
    <Stack gap={1}>
      <Stack px={3} pt={3} gap={1}>
        <Typography variant="h4" fontWeight="bold">材料ロット QRコード読み取り</Typography>
        <Alert severity="info" sx={{ py: 0.5 }}>
          材料 <b>{matName}</b> / LotNo <b>{lotNo}</b> 分の QR を <b>{readyLot.material_count} 枚</b> 読み取ります。スキャンした QR はこのロットに直接紐付きます。
        </Alert>
      </Stack>
      <QRCodeInput state={input.getState("MaterialIDs")} onFinished={(value) => process(value ?? [])} onCancel={() => navigate(-1)} />
    </Stack>
  );
}
